/**
 * On-demand translation for clinical note fields (Doctor Notes / Discharge Summary), backed by
 * LibreTranslate (backend/src/translate/translate.module.ts) — a free, open-source translation
 * engine, not a paid/proprietary AI service. Self-hosting it keeps clinical text entirely on
 * the hospital's own network; see .env.example for setup.
 *
 * Deliberately read-only: shows the translation in a preview modal with a
 * "verify before relying on this" disclaimer and a copy-to-clipboard action, but has no
 * "insert"/"replace" button — it can never silently overwrite a clinician's original note.
 *
 * NOTE: this button is always shown (as long as there's text to translate) rather than trying
 * to pre-detect whether LIBRETRANSLATE_URL is configured. An earlier version hid the button
 * permanently, page-wide, the first time it got a 503 — which meant that fixing the backend
 * config (adding the URL, restarting the server) had no visible effect until a full browser
 * reload, which looked exactly like "the setting isn't being picked up" even when it was.
 * Now a 503 just shows an inline "not enabled" message in the modal, same as any other failure,
 * so every click reflects the backend's CURRENT state — no stale client-side memory involved.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';
import Modal from './Modal';

const TRANSLATE_TARGETS = [
  { code: 'en', label: 'English' },
  { code: 'sw', label: 'Kiswahili' },
  { code: 'fr', label: 'Français' },
];

export default function TranslateButton({ text }: { text: string }) {
  const { t, i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const [target, setTarget] = useState(() => (i18n.language === 'en' ? 'sw' : 'en'));
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<{ translatedText: string; disclaimer?: string } | null>(null);
  const [error, setError] = useState('');

  const runTranslate = async (targetLang: string) => {
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await api.translateText(text, targetLang, i18n.language);
      setResult(res.data);
    } catch (e: any) {
      if (e?.response?.status === 503) setError(t('common.translateUnavailable', 'Translation is not enabled on this server yet (check LIBRETRANSLATE_URL and restart the backend).'));
      else setError(t('common.translateFailed', 'Translation failed. Please try again.'));
    } finally { setLoading(false); }
  };

  const openModal = () => { setOpen(true); runTranslate(target); };

  if (!text?.trim()) return null;

  return (
    <>
      <button type="button" onClick={openModal} title={t('common.translate', 'Translate')}
        className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-sm bg-slate-100 text-slate-400 hover:bg-blue-100 hover:text-[#02357A] transition-all">
        🌐
      </button>
      <Modal isOpen={open} onClose={() => setOpen(false)} title={t('common.translationTitle', '🌐 Translation')} size="md">
        <div className="space-y-3">
          <div className="flex items-center gap-2">
            <label className="text-xs font-bold text-slate-500 uppercase tracking-wide">{t('common.translateInto', 'Translate into')}</label>
            <select className="border-2 border-slate-200 rounded-lg px-2 py-1 text-sm" value={target}
              onChange={e => { setTarget(e.target.value); runTranslate(e.target.value); }}>
              {TRANSLATE_TARGETS.map(l => <option key={l.code} value={l.code}>{l.label}</option>)}
            </select>
          </div>
          {loading && <p className="text-sm text-slate-400">{t('common.translating', 'Translating…')}</p>}
          {error && <p className="text-sm text-red-600">{error}</p>}
          {result && !loading && !error && (
            <>
              <div className="bg-slate-50 border border-slate-200 rounded-xl p-3 text-sm whitespace-pre-wrap">{result.translatedText || '—'}</div>
              <p className="text-xs text-amber-600">⚠️ {result.disclaimer || t('common.translateDisclaimer', 'Machine translation — verify clinical accuracy before relying on this.')}</p>
              <div className="flex justify-end">
                <button type="button" onClick={() => navigator.clipboard?.writeText(result.translatedText)}
                  className="text-xs font-semibold text-[#02357A] hover:underline">{t('common.copyToClipboard', 'Copy to clipboard')}</button>
              </div>
            </>
          )}
        </div>
      </Modal>
    </>
  );
}
